import { Controller } from "@hotwired/stimulus"
import { Modal, Dropdown } from "bootstrap"

export default class extends Controller {
  static targets = ["focusable", "skipLink", "announcement"]

  connect() {
    this.initializeKeyboardNavigation()
    this.initializeScreenReaderSupport()
    this.initializeFocusManagement()
    this.setupSkipLinks()
  }

  initializeKeyboardNavigation() {
    // Add keyboard event listeners for navigation
    this.element.addEventListener('keydown', this.handleKeyboardNavigation.bind(this))
    
    // Set up tab order for complex components
    this.setupTabOrder()
    
    // Add keyboard shortcuts
    this.setupKeyboardShortcuts()
  }

  handleKeyboardNavigation(event) {
    const { key, ctrlKey, altKey, shiftKey } = event
    
    // Handle arrow key navigation in lists
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
      this.handleArrowKeyNavigation(event)
    }
    
    // Handle Enter and Space for activation
    if (key === 'Enter' || key === ' ') {
      this.handleActivation(event)
    }
    
    // Handle Escape for closing modals/dropdowns
    if (key === 'Escape') {
      this.handleEscape(event)
    }
    
    // Handle Tab for focus management
    if (key === 'Tab') {
      this.handleTabNavigation(event)
    }
  }

  handleArrowKeyNavigation(event) {
    const { key, target } = event
    const currentElement = target
    
    // Handle navigation in field lists
    if (currentElement.closest('.field-picker-container, .selected-fields-container')) {
      event.preventDefault()
      this.navigateFieldList(currentElement, key)
    }
    
    // Handle navigation in filter lists
    if (currentElement.closest('.filter-container')) {
      event.preventDefault()
      this.navigateFilterList(currentElement, key)
    }
    
    // Handle navigation in chart options
    if (currentElement.closest('.chart-customization-panel')) {
      event.preventDefault()
      this.navigateChartOptions(currentElement, key)
    }
  }

  navigateFieldList(currentElement, key) {
    const container = currentElement.closest('.field-picker-container, .selected-fields-container')
    const items = Array.from(container.querySelectorAll('.field-item, .selected-field-item'))
    const currentIndex = items.findIndex(item => item.contains(currentElement))
    
    let nextIndex
    if (key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
    } else if (key === 'ArrowDown') {
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
    }
    
    if (nextIndex !== undefined && items[nextIndex]) {
      const nextFocusable = items[nextIndex].querySelector('input, button, [tabindex]')
      if (nextFocusable) {
        nextFocusable.focus()
        this.announceNavigation(`Moved to ${this.getElementDescription(items[nextIndex])}`)
      }
    }
  }

  navigateFilterList(currentElement, key) {
    const container = currentElement.closest('.filter-container')
    const items = Array.from(container.querySelectorAll('.filter-item'))
    const currentIndex = items.findIndex(item => item.contains(currentElement))
    
    let nextIndex
    if (key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
    } else if (key === 'ArrowDown') {
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
    }
    
    if (nextIndex !== undefined && items[nextIndex]) {
      const nextFocusable = items[nextIndex].querySelector('select, input, button')
      if (nextFocusable) {
        nextFocusable.focus()
        this.announceNavigation(`Moved to filter ${nextIndex + 1}`)
      }
    }
  }

  navigateChartOptions(currentElement, key) {
    const container = currentElement.closest('.chart-customization-panel')
    const controls = Array.from(container.querySelectorAll('select, input, button'))
    const currentIndex = controls.indexOf(currentElement)
    
    let nextIndex
    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : controls.length - 1
    } else if (key === 'ArrowRight' || key === 'ArrowDown') {
      nextIndex = currentIndex < controls.length - 1 ? currentIndex + 1 : 0
    }
    
    if (nextIndex !== undefined && controls[nextIndex]) {
      controls[nextIndex].focus()
      this.announceNavigation(`Moved to ${this.getElementDescription(controls[nextIndex])}`)
    }
  }

  handleActivation(event) {
    const { target } = event
    
    // Handle activation of custom elements that aren't naturally focusable
    if (target.classList.contains('field-item') && !target.querySelector('input:focus')) {
      event.preventDefault()
      const checkbox = target.querySelector('input[type="checkbox"]')
      if (checkbox) {
        checkbox.click()
        this.announceAction(`${checkbox.checked ? 'Selected' : 'Deselected'} field ${this.getFieldName(target)}`)
      }
    }
    
    // Handle activation of drag handles
    if (target.classList.contains('fa-grip-vertical') || target.closest('.grip-handle')) {
      event.preventDefault()
      this.initiateDragMode(target)
    }
  }

  handleEscape(event) {
    // Close any open modals
    const openModal = document.querySelector('.modal.show')
    if (openModal) {
      const modal = Modal.getInstance(openModal)
      if (modal) {
        modal.hide()
        this.announceAction('Modal closed')
      }
    }
    
    // Close any open dropdowns
    const openDropdown = document.querySelector('.dropdown-menu.show')
    if (openDropdown) {
      const dropdown = Dropdown.getInstance(openDropdown.previousElementSibling)
      if (dropdown) {
        dropdown.hide()
        this.announceAction('Dropdown closed')
      }
    }
    
    // Exit drag mode if active
    if (this.dragMode) {
      this.exitDragMode()
    }
  }

  handleTabNavigation(event) {
    // Ensure proper tab order in complex components
    const { target, shiftKey } = event
    
    // Handle tab trapping in modals
    const modal = target.closest('.modal')
    if (modal) {
      this.handleModalTabTrapping(event, modal)
    }
    
    // Handle tab order in card layouts
    const card = target.closest('.card')
    if (card) {
      this.handleCardTabOrder(event, card)
    }
  }

  handleModalTabTrapping(event, modal) {
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstFocusable = focusableElements[0]
    const lastFocusable = focusableElements[focusableElements.length - 1]
    
    if (event.shiftKey && event.target === firstFocusable) {
      event.preventDefault()
      lastFocusable.focus()
    } else if (!event.shiftKey && event.target === lastFocusable) {
      event.preventDefault()
      firstFocusable.focus()
    }
  }

  setupTabOrder() {
    // Set logical tab order for the report builder
    const sections = [
      '.data-source-selector',
      '.field-picker-container',
      '.selected-fields-container',
      '.filter-container',
      '.chart-builder-section',
      '.preview-section'
    ]
    
    sections.forEach((selector, index) => {
      const section = this.element.querySelector(selector)
      if (section) {
        section.setAttribute('data-tab-section', index + 1)
      }
    })
  }

  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (event) => {
      const { key, ctrlKey, altKey } = event
      
      // Ctrl+S: Save template
      if (ctrlKey && key === 's') {
        event.preventDefault()
        this.triggerSaveTemplate()
      }
      
      // Ctrl+R: Refresh preview
      if (ctrlKey && key === 'r') {
        event.preventDefault()
        this.triggerRefreshPreview()
      }
      
      // Ctrl+E: Export report
      if (ctrlKey && key === 'e') {
        event.preventDefault()
        this.triggerExport()
      }
      
      // Alt+1-6: Navigate to sections
      if (altKey && ['1', '2', '3', '4', '5', '6'].includes(key)) {
        event.preventDefault()
        this.navigateToSection(parseInt(key))
      }
      
      // F1: Show help
      if (key === 'F1') {
        event.preventDefault()
        this.showHelp()
      }
    })
  }

  triggerSaveTemplate() {
    const saveButton = document.querySelector('[data-action*="saveTemplate"]')
    if (saveButton) {
      saveButton.click()
      this.announceAction('Save template dialog opened')
    }
  }

  triggerRefreshPreview() {
    const refreshButton = document.querySelector('[data-action*="updatePreview"]')
    if (refreshButton) {
      refreshButton.click()
      this.announceAction('Preview refreshed')
    }
  }

  triggerExport() {
    const exportButton = document.querySelector('[data-action*="generateFullReport"]')
    if (exportButton) {
      exportButton.click()
      this.announceAction('Report export started')
    }
  }

  navigateToSection(sectionNumber) {
    const section = this.element.querySelector(`[data-tab-section="${sectionNumber}"]`)
    if (section) {
      const firstFocusable = section.querySelector('input, select, button, [tabindex]:not([tabindex="-1"])')
      if (firstFocusable) {
        firstFocusable.focus()
        this.announceNavigation(`Navigated to section ${sectionNumber}`)
      }
    }
  }

  showHelp() {
    // Show keyboard shortcuts help
    this.announceAction('Keyboard shortcuts: Ctrl+S to save, Ctrl+R to refresh, Ctrl+E to export, Alt+1-6 to navigate sections, F1 for help')
  }

  initializeScreenReaderSupport() {
    // Add ARIA labels and descriptions
    this.addAriaLabels()
    
    // Set up live regions for announcements
    this.setupLiveRegions()
    
    // Add role attributes where needed
    this.addRoleAttributes()
  }

  addAriaLabels() {
    // Add labels to form controls without explicit labels
    const unlabeledInputs = this.element.querySelectorAll('input:not([aria-label]):not([aria-labelledby])')
    unlabeledInputs.forEach(input => {
      const placeholder = input.getAttribute('placeholder')
      if (placeholder) {
        input.setAttribute('aria-label', placeholder)
      }
    })
    
    // Add labels to buttons without text
    const iconButtons = this.element.querySelectorAll('button:not([aria-label]) i.fas')
    iconButtons.forEach(icon => {
      const button = icon.closest('button')
      if (button && !button.textContent.trim()) {
        const iconClass = Array.from(icon.classList).find(cls => cls.startsWith('fa-'))
        if (iconClass) {
          const action = iconClass.replace('fa-', '').replace('-', ' ')
          button.setAttribute('aria-label', action)
        }
      }
    })
  }

  setupLiveRegions() {
    // Create announcement region if it doesn't exist
    if (!this.hasAnnouncementTarget) {
      const announcementRegion = document.createElement('div')
      announcementRegion.setAttribute('aria-live', 'polite')
      announcementRegion.setAttribute('aria-atomic', 'true')
      announcementRegion.setAttribute('data-accessibility-target', 'announcement')
      announcementRegion.className = 'sr-only'
      this.element.appendChild(announcementRegion)
    }
    
    // Create status region for form validation
    const statusRegion = document.createElement('div')
    statusRegion.setAttribute('aria-live', 'assertive')
    statusRegion.setAttribute('aria-atomic', 'true')
    statusRegion.className = 'sr-only'
    statusRegion.id = 'form-status'
    this.element.appendChild(statusRegion)
  }

  addRoleAttributes() {
    // Add list roles to field containers
    const fieldContainers = this.element.querySelectorAll('.field-picker-container, .selected-fields-container')
    fieldContainers.forEach(container => {
      container.setAttribute('role', 'listbox')
      container.setAttribute('aria-multiselectable', 'true')
      
      const items = container.querySelectorAll('.field-item, .selected-field-item')
      items.forEach(item => {
        item.setAttribute('role', 'option')
        item.setAttribute('tabindex', '0')
      })
    })
    
    // Add group roles to sections
    const sections = this.element.querySelectorAll('.card')
    sections.forEach(section => {
      const title = section.querySelector('.card-title')
      if (title) {
        const titleId = `section-${Math.random().toString(36).substr(2, 9)}`
        title.id = titleId
        section.setAttribute('role', 'group')
        section.setAttribute('aria-labelledby', titleId)
      }
    })
  }

  initializeFocusManagement() {
    // Manage focus for dynamic content
    this.setupFocusTraps()
    this.setupFocusRestoration()
    
    // Add focus indicators
    this.addFocusIndicators()
  }

  setupFocusTraps() {
    // Focus trap for modals is handled in handleTabNavigation
    // Add focus trap for dropdown menus
    const dropdowns = this.element.querySelectorAll('.dropdown-menu')
    dropdowns.forEach(dropdown => {
      dropdown.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') {
          this.handleDropdownTabTrapping(event, dropdown)
        }
      })
    })
  }

  handleDropdownTabTrapping(event, dropdown) {
    const focusableElements = dropdown.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])')
    const firstFocusable = focusableElements[0]
    const lastFocusable = focusableElements[focusableElements.length - 1]
    
    if (event.shiftKey && event.target === firstFocusable) {
      event.preventDefault()
      lastFocusable.focus()
    } else if (!event.shiftKey && event.target === lastFocusable) {
      event.preventDefault()
      firstFocusable.focus()
    }
  }

  setupFocusRestoration() {
    // Store focus before opening modals
    document.addEventListener('show.bs.modal', (event) => {
      this.previousFocus = document.activeElement
    })
    
    // Restore focus after closing modals
    document.addEventListener('hidden.bs.modal', (event) => {
      if (this.previousFocus) {
        this.previousFocus.focus()
        this.previousFocus = null
      }
    })
  }

  addFocusIndicators() {
    // Add custom focus indicators for better visibility
    const style = document.createElement('style')
    style.textContent = `
      .accessibility-focus-indicator:focus {
        outline: 3px solid #007bff !important;
        outline-offset: 2px !important;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25) !important;
      }
      
      .field-item:focus,
      .selected-field-item:focus {
        background-color: #e7f3ff !important;
        border-color: #007bff !important;
      }
    `
    document.head.appendChild(style)
    
    // Add focus indicator class to interactive elements
    const interactiveElements = this.element.querySelectorAll(
      'button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    interactiveElements.forEach(element => {
      element.classList.add('accessibility-focus-indicator')
    })
  }

  setupSkipLinks() {
    // Create skip links for keyboard navigation
    const skipLinksContainer = document.createElement('div')
    skipLinksContainer.className = 'skip-links sr-only-focusable'
    skipLinksContainer.innerHTML = `
      <a href="#data-source-section" class="btn btn-primary btn-sm">Skip to Data Source</a>
      <a href="#fields-section" class="btn btn-primary btn-sm">Skip to Fields</a>
      <a href="#filters-section" class="btn btn-primary btn-sm">Skip to Filters</a>
      <a href="#chart-section" class="btn btn-primary btn-sm">Skip to Chart</a>
      <a href="#preview-section" class="btn btn-primary btn-sm">Skip to Preview</a>
    `
    
    // Insert at the beginning of the element
    this.element.insertBefore(skipLinksContainer, this.element.firstChild)
    
    // Add IDs to sections for skip links
    const sections = [
      { selector: '.data-source-selector', id: 'data-source-section' },
      { selector: '.field-picker-container', id: 'fields-section' },
      { selector: '.filter-container', id: 'filters-section' },
      { selector: '.chart-builder-section', id: 'chart-section' },
      { selector: '.preview-section', id: 'preview-section' }
    ]
    
    sections.forEach(({ selector, id }) => {
      const section = this.element.querySelector(selector)
      if (section) {
        section.id = id
      }
    })
  }

  // Drag mode for keyboard users
  initiateDragMode(element) {
    this.dragMode = true
    this.dragElement = element.closest('.field-item, .selected-field-item')
    
    if (this.dragElement) {
      this.dragElement.classList.add('keyboard-drag-mode')
      this.announceAction('Drag mode activated. Use arrow keys to move, Enter to drop, Escape to cancel')
      
      // Set up drag mode event listeners
      this.setupDragModeListeners()
    }
  }

  setupDragModeListeners() {
    this.dragModeHandler = (event) => {
      if (!this.dragMode) return
      
      const { key } = event
      
      if (['ArrowUp', 'ArrowDown'].includes(key)) {
        event.preventDefault()
        this.moveDragElement(key)
      } else if (key === 'Enter') {
        event.preventDefault()
        this.dropDragElement()
      } else if (key === 'Escape') {
        event.preventDefault()
        this.exitDragMode()
      }
    }
    
    document.addEventListener('keydown', this.dragModeHandler)
  }

  moveDragElement(direction) {
    if (!this.dragElement) return
    
    const container = this.dragElement.parentElement
    const siblings = Array.from(container.children)
    const currentIndex = siblings.indexOf(this.dragElement)
    
    let newIndex
    if (direction === 'ArrowUp' && currentIndex > 0) {
      newIndex = currentIndex - 1
    } else if (direction === 'ArrowDown' && currentIndex < siblings.length - 1) {
      newIndex = currentIndex + 1
    }
    
    if (newIndex !== undefined) {
      const targetElement = siblings[newIndex]
      if (direction === 'ArrowUp') {
        container.insertBefore(this.dragElement, targetElement)
      } else {
        container.insertBefore(this.dragElement, targetElement.nextSibling)
      }
      
      this.announceAction(`Moved ${direction === 'ArrowUp' ? 'up' : 'down'}`)
    }
  }

  dropDragElement() {
    if (this.dragElement) {
      this.announceAction('Element dropped')
      this.exitDragMode()
      
      // Trigger any necessary updates
      const event = new CustomEvent('field-reordered', {
        detail: { element: this.dragElement },
        bubbles: true
      })
      this.dragElement.dispatchEvent(event)
    }
  }

  exitDragMode() {
    if (this.dragElement) {
      this.dragElement.classList.remove('keyboard-drag-mode')
      this.dragElement.focus()
    }
    
    this.dragMode = false
    this.dragElement = null
    
    if (this.dragModeHandler) {
      document.removeEventListener('keydown', this.dragModeHandler)
      this.dragModeHandler = null
    }
    
    this.announceAction('Drag mode exited')
  }

  // Utility methods
  announceAction(message) {
    if (this.hasAnnouncementTarget) {
      this.announcementTarget.textContent = message
    }
  }

  announceNavigation(message) {
    if (this.hasAnnouncementTarget) {
      this.announcementTarget.textContent = message
    }
  }

  getElementDescription(element) {
    // Get a descriptive name for an element for screen readers
    const label = element.querySelector('label, .form-label')
    if (label) {
      return label.textContent.trim()
    }
    
    const title = element.querySelector('.card-title, h1, h2, h3, h4, h5, h6')
    if (title) {
      return title.textContent.trim()
    }
    
    const ariaLabel = element.getAttribute('aria-label')
    if (ariaLabel) {
      return ariaLabel
    }
    
    return element.textContent.trim().substring(0, 50) || 'Interactive element'
  }

  getFieldName(fieldElement) {
    const label = fieldElement.querySelector('strong')
    return label ? label.textContent.trim() : 'field'
  }
}